import React from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ProgressBar,
  Row,
  Spinner,
} from 'react-bootstrap';
import {
  Dac7ReportResponse,
  WinTimeImportResponse,
} from '../../../common/api/dac7Service';
import { LOADING_STATUS } from '../../../common/constants';
import {
  Dac7CompletionUpdate,
  Dac7ProgressUpdate,
} from '../../../common/hubs/signalRDac7Service';
import {
  getPendingRows,
  getRemovalRows,
  getReprocessingRows,
} from '../dac7ViewUtils';

type ProcessWinTimeCardProps = {
  reports: Dac7ReportResponse[];
  processReportId: string;
  winTimeImports: WinTimeImportResponse[];
  winTimeImportsStatus: LOADING_STATUS;
  isProcessing: boolean;
  processingProgress: Dac7ProgressUpdate | null;
  processingComplete: Dac7CompletionUpdate | null;
  onProcessReportChange: (value: string) => void;
  onProcess: () => void;
  onDismissProcessingComplete: () => void;
};

function ProcessWinTimeCard({
  reports,
  processReportId,
  winTimeImports,
  winTimeImportsStatus,
  isProcessing,
  processingProgress,
  processingComplete,
  onProcessReportChange,
  onProcess,
  onDismissProcessingComplete,
}: ProcessWinTimeCardProps) {
  const pendingRows = getPendingRows(winTimeImports);
  const reprocessingRows = getReprocessingRows(winTimeImports);
  const removalRows = getRemovalRows(winTimeImports);
  const canProcess = pendingRows > 0 || reprocessingRows > 0 || removalRows > 0;

  return (
    <Card className="bg-white border shadow-none p-24 mb-24">
      <h5 className="mb-16">Käsittele WinTime-tiedot</h5>
      <p className="text-muted mb-16">
        Käsittele tuodut WinTime-tiedot ja luo myyjät sekä kohteet
        DAC7-raporttiin. Tämä yhdistää tiedot InfoBooking-järjestelmän
        tietoihin.
      </p>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-16">
            <Form.Label>Valitse raportti käsiteltäväksi</Form.Label>
            <Form.Select
              value={processReportId}
              onChange={e => onProcessReportChange(e.target.value)}
              disabled={isProcessing}
            >
              <option value="">-- Valitse raportti --</option>
              {reports.map(report => (
                <option key={report.id} value={report.id}>
                  {report.reportingYear} - {report.messageRefId}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col
          md={3}
          className="d-flex align-items-end flex-column justify-content-end"
        >
          {processReportId &&
            winTimeImportsStatus === LOADING_STATUS.LOADING && (
              <div className="mb-8 text-muted">
                <Spinner size="sm" className="me-2" />
                Ladataan tuontitietoja...
              </div>
            )}
          {processReportId &&
            winTimeImportsStatus === LOADING_STATUS.IDLE &&
            !isProcessing && (
              <div className="mb-8">
                {pendingRows > 0 && (
                  <Badge bg="info">{pendingRows} käsittelemätöntä riviä</Badge>
                )}
                {pendingRows === 0 && reprocessingRows > 0 && (
                  <Badge bg="warning">{reprocessingRows} korjattua riviä</Badge>
                )}
                {pendingRows === 0 && removalRows > 0 && (
                  <Badge bg="danger">{removalRows} poistettavaa riviä</Badge>
                )}
                {pendingRows === 0 &&
                  reprocessingRows === 0 &&
                  removalRows === 0 && (
                    <Badge bg="secondary">Ei käsittelemättömiä rivejä</Badge>
                  )}
              </div>
            )}
          <Button
            variant="warning"
            onClick={onProcess}
            disabled={
              isProcessing ||
              !processReportId ||
              winTimeImportsStatus === LOADING_STATUS.LOADING ||
              !canProcess
            }
            className="mb-16 w-100"
          >
            {isProcessing ? (
              <>
                <Spinner size="sm" className="me-2" />
                Käsitellään...
              </>
            ) : (
              'Käsittele tiedot'
            )}
          </Button>
        </Col>
      </Row>

      {isProcessing && processingProgress && (
        <Row className="mt-16">
          <Col>
            <div className="mb-8">
              <strong>{processingProgress.phase}:</strong>{' '}
              {processingProgress.message}
            </div>
            <ProgressBar
              now={processingProgress.currentItem}
              max={processingProgress.totalItems}
              label={`${processingProgress.currentItem}%`}
              animated
              striped
            />
          </Col>
        </Row>
      )}

      {isProcessing && !processingProgress && (
        <Row className="mt-16">
          <Col>
            <Alert variant="info">
              <Spinner size="sm" className="me-2" />
              Käsittely käynnistyy hetken kuluttua...
            </Alert>
          </Col>
        </Row>
      )}

      {processingComplete && (
        <Row className="mt-16">
          <Col>
            <Alert
              variant={processingComplete.success ? 'success' : 'danger'}
              dismissible
              onClose={onDismissProcessingComplete}
            >
              <Alert.Heading>
                {processingComplete.success
                  ? 'Käsittely valmis!'
                  : 'Käsittely epäonnistui'}
              </Alert.Heading>
              <p>{processingComplete.message}</p>
              {processingComplete.success && (
                <ul className="mb-0">
                  <li>Myyjiä luotu: {processingComplete.sellersCreated}</li>
                  <li>
                    Epäonnistuneet wintime import rivit:{' '}
                    {processingComplete.failedRows}
                  </li>
                  <li>
                    Kohteita luotu: {processingComplete.propertiesCreated}
                  </li>
                  <li>
                    Onnistuneesti yhdistetty: {processingComplete.matchedRows}
                  </li>
                </ul>
              )}
            </Alert>
          </Col>
        </Row>
      )}
    </Card>
  );
}

export default ProcessWinTimeCard;
